import { useState, useEffect, useRef, useCallback } from 'react';

const CAST_SDK_URL = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';

// Loads the Google Cast Web Sender SDK once and resolves when it's ready.
let castApiPromise = null;
function loadCastApi() {
  if (castApiPromise) return castApiPromise;
  castApiPromise = new Promise((resolve, reject) => {
    if (window.chrome?.cast?.isAvailable) {
      resolve();
      return;
    }
    const prevCallback = window['__onGCastApiAvailable'];
    window['__onGCastApiAvailable'] = (isAvailable) => {
      prevCallback?.(isAvailable);
      if (isAvailable) resolve();
      else reject(new Error('Cast API unavailable'));
    };
    if (!document.getElementById('cast-sender-sdk')) {
      const script = document.createElement('script');
      script.id = 'cast-sender-sdk';
      script.src = CAST_SDK_URL;
      script.async = true;
      document.head.appendChild(script);
    }
  });
  return castApiPromise;
}

// Sender-side hook for casting images to a Chromecast/Android TV via the
// Default Media Receiver (no custom receiver app required).
export function useChromecastSender() {
  const [available, setAvailable] = useState(false);
  const [connected, setConnected] = useState(false);
  const sessionRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadCastApi().then(() => {
      if (cancelled) return;
      const context = window.cast.framework.CastContext.getInstance();
      context.setOptions({
        receiverApplicationId: window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
        autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
      });
      setAvailable(true);

      const updateSession = () => {
        const session = context.getCurrentSession();
        sessionRef.current = session;
        setConnected(!!session);
      };
      updateSession();
      context.addEventListener(
        window.cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
        updateSession
      );
    }).catch(() => setAvailable(false));
    return () => { cancelled = true; };
  }, []);

  const requestSession = useCallback(() => {
    if (!window.cast?.framework) return Promise.reject(new Error('Cast not available'));
    return window.cast.framework.CastContext.getInstance().requestSession();
  }, []);

  const endSession = useCallback(() => {
    if (!window.cast?.framework) return;
    window.cast.framework.CastContext.getInstance().endCurrentSession(true);
  }, []);

  const loadImage = useCallback((url, title) => {
    const session = sessionRef.current;
    if (!session || !url) return;
    const mediaInfo = new window.chrome.cast.media.MediaInfo(url, 'image/jpeg');
    mediaInfo.metadata = new window.chrome.cast.media.GenericMediaMetadata();
    mediaInfo.metadata.title = title || '';
    const request = new window.chrome.cast.media.LoadRequest(mediaInfo);
    session.loadMedia(request).catch((err) => console.error('Cast loadMedia failed', err));
  }, []);

  const toggleCast = useCallback(() => {
    if (connected) endSession();
    else requestSession().catch((err) => {
      // User cancelled the device picker, or no devices found.
      if (err?.code !== 'cancel') console.error('Cast session request failed', err);
    });
  }, [connected, endSession, requestSession]);

  return { available, connected, toggleCast, loadImage };
}
